import {
  boPhanSearchColumns,
  chiPhiKhongHopLeSearchColumns,
  dotThanhToanSearchColumns,
  hopDongSearchColumns,
  kheUocSearchColumns,
  lenhSanXuatSearchColumns,
  phiSearchColumns,
  QUERY_KEYS,
  thueSearchColumns,
  vatTu1SearchColumns,
  vatTuSearchColumns,
  vuViecSearchColumns,
  warehouseSearchColumns
} from '@/constants';
import {
  CellFieldV2 as CellField,
  DvtFieldV2 as DvtField,
  SearchFieldV2 as SearchField
} from '@/components/custom/erp';
import { BoPhan, DotThanhToan, HopDong, KheUoc, KhoHang, Phi, type Tax, VatTu, VuViec } from '@/types/schemas';
import type { DataTableRenderCellParams } from '@/components/custom/erp/data-table-v2';

export const getDetailTableColumns = (
  onCellValueChange: (rowUuid: string, field: string, newValue: BanHangValue) => void,
  fetchDetail: (params: BanHangTableParams) => void,
  cellError?: (rowUuid: string, field: string) => string | undefined
) => [
  {
    field: 'ma_vt',
    headerName: 'Mã sản phẩm',
    width: 200,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<VatTu>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.VAT_TU}/`}
        searchColumns={vatTu1SearchColumns}
        columnDisplay='ma_vt'
        dialogTitle='Danh mục vật tư'
        value={params.row.ma_vt_data?.ma_vt || ''}
        onRowSelection={async (row: VatTu) => {
          const data = await fetchDetail(row?.uuid || '');
          onCellValueChange(params.row.uuid, 'ma_vt_data', data);
        }}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_vt',
      resultField: 'ma_vt_data',
      detailEndpoint: QUERY_KEYS.VAT_TU
    }
  },
  {
    field: 'ten_vt',
    headerName: 'Tên sản phẩm',
    width: 200,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField name='ten_vt' type='text' value={params.row.ma_vt_data?.ten_vt || ''} />
    ),
    nonWritable: true
  },
  {
    field: 'dvt',
    headerName: 'Đvt',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => (
      <DvtField row={params.row} onCellValueChange={onCellValueChange} />
    ),
    nonWritable: true
  },
  {
    field: 'ma_kho',
    headerName: 'Mã kho',
    width: 150,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<KhoHang>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.KHO_HANG}/`}
        searchColumns={warehouseSearchColumns}
        columnDisplay='ma_kho'
        dialogTitle='Danh mục kho hàng'
        value={params.row.ma_kho_data?.ma_kho || params.row.ma_vt_data?.ma_kho_data?.ma_kho || ''}
        onRowSelection={(row: KhoHang) => onCellValueChange(params.row.uuid, 'ma_kho_data', row)}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_kho',
      resultField: 'ma_kho_data',
      detailEndpoint: QUERY_KEYS.KHO_HANG
    }
  },
  {
    field: 'ma_khon',
    headerName: 'Mã kho nhận',
    width: 150,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<KhoHang>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.KHO_HANG}/`}
        searchColumns={warehouseSearchColumns}
        columnDisplay='ma_kho'
        dialogTitle='Danh mục kho hàng'
        value={params.row.ma_khon_data?.ma_kho || params.row.ma_khon || ''}
        onRowSelection={(row: KhoHang) => onCellValueChange(params.row.uuid, 'ma_khon_data', row)}
        error={cellError?.(params.row.uuid, 'ma_khon')}
        errorDisplay='tooltip'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_khon',
      resultField: 'ma_khon_data',
      detailEndpoint: QUERY_KEYS.KHO_HANG
    }
  },
  {
    field: 'sl_ton',
    headerName: 'Tồn',
    width: 150,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField name='sl_ton' type='number' value={params.row.sl_ton ?? 0} disabled={true} />
    ),
    nonWritable: true
  },
  {
    field: 'so_luong',
    headerName: 'Số lượng',
    width: 100,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField
        name='so_luong'
        type='number'
        placeholder='0'
        value={params.row.so_luong}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'so_luong', newValue)}
      />
    )
  },
  {
    field: 'khoi_luong',
    headerName: 'Khối lượng',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField
        name='khoi_luong'
        type='number'
        placeholder='0'
        value={params.row.khoi_luong}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'khoi_luong', newValue)}
      />
    ),
    nonWritable: true
  },
  {
    field: 'ct_km',
    headerName: 'Loại hàng',
    width: 150,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField
        name='ct_km'
        type='select'
        options={[
          { value: '0', label: '0. Hàng bán' },
          { value: '1', label: '1. Hàng KM' }
        ]}
        value={params.row.ct_km}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'ct_km', newValue)}
      />
    )
  },
  {
    field: 'gia_nt1',
    headerName: 'Giá chuẩn VND',
    width: 150,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField
        name='gia_nt1'
        type='number'
        placeholder='0'
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'gia_nt1', newValue)}
        value={params.row.gia_nt1}
      />
    ),
    nonWritable: true
  },
  {
    field: 'gia_nt2',
    headerName: 'Giá bán VND',
    width: 150,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField
        name='gia_nt2'
        type='number'
        placeholder='0'
        value={params.row.gia_nt2}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'gia_nt2', newValue)}
      />
    )
  },
  {
    field: 'tien_nt2',
    headerName: 'Thành tiền VND',
    width: 150,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField
        name='tien_nt2'
        type='number'
        value={params.row.tien_nt2}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'tien_nt2', newValue)}
      />
    )
  },
  {
    field: 'ngay_giao',
    headerName: 'Ngày giao',
    width: 150,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField
        name='ngay_giao'
        type='date'
        value={params.row.ngay_giao || null}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'ngay_giao', newValue)}
      />
    )
  },
  {
    field: 'tl_ck',
    headerName: 'Tl ck(%)',
    width: 150,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField
        name='tl_ck'
        type='number'
        value={params.row.tl_ck || ''}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'tl_ck', newValue)}
      />
    )
  },
  {
    field: 'ck_nt',
    headerName: 'Ch.khấu VND',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField
        name='ck_nt'
        type='number'
        placeholder='0'
        value={params.row.ck_nt}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'ck_nt', newValue)}
      />
    )
  },
  {
    field: 'ma_thue',
    headerName: 'Thuế suất',
    width: 150,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<Tax>
        searchEndpoint={`/${QUERY_KEYS.THUE}/`}
        searchColumns={thueSearchColumns}
        dialogTitle='Danh mục thuế suất'
        value={params.row.ma_thue_data?.ten_thue}
        onRowSelection={(row: BanHangRecord) => onCellValueChange?.(params.row.uuid, 'ma_thue_data', row)}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_thue',
      resultField: 'ma_thue_data',
      detailEndpoint: QUERY_KEYS.THUE
    }
  },
  {
    field: 'thue_nt',
    headerName: 'Thuế VND',
    width: 150,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField
        name='thue_nt'
        type='number'
        value={params.row.thue_nt}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'thue_nt', newValue)}
      />
    )
  },
  {
    field: 'ma_bp',
    headerName: 'Bộ phận',
    width: 150,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<BoPhan>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.BO_PHAN}/`}
        searchColumns={boPhanSearchColumns}
        columnDisplay='ma_bp'
        dialogTitle='Danh mục bộ phận'
        value={params.row.ma_bp_data?.ma_bp || ''}
        onRowSelection={(row: BanHangRecord) => onCellValueChange(params.row.uuid, 'ma_bp_data', row)}
        error={cellError?.(params.row.uuid, 'ma_bp')}
        errorDisplay='tooltip'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_bp',
      resultField: 'ma_bp_data',
      detailEndpoint: QUERY_KEYS.BO_PHAN
    }
  },
  {
    field: 'ma_vv',
    headerName: 'Vụ việc',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<VuViec>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.VU_VIEC}/`}
        searchColumns={vuViecSearchColumns}
        columnDisplay='ma_vu_viec'
        dialogTitle='Danh mục vụ việc'
        value={params.row.ma_vv_data?.ma_vu_viec || ''}
        onRowSelection={(row: BanHangRecord) => onCellValueChange(params.row.uuid, 'ma_vv_data', row)}
        error={cellError?.(params.row.uuid, 'ma_vv')}
        errorDisplay='tooltip'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_vv',
      resultField: 'ma_vv_data',
      detailEndpoint: QUERY_KEYS.VU_VIEC
    }
  },
  {
    field: 'ma_hd',
    headerName: 'Hợp đồng',
    width: 150,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<HopDong>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.HOP_DONG}/`}
        searchColumns={hopDongSearchColumns}
        columnDisplay='ma_hd'
        dialogTitle='Danh mục hợp đồng'
        value={params.row.ma_hd_data?.ma_hd || ''}
        onRowSelection={(row: BanHangRecord) => onCellValueChange(params.row.uuid, 'ma_hd_data', row)}
        error={cellError?.(params.row.uuid, 'ma_hd')}
        errorDisplay='tooltip'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_hd',
      resultField: 'ma_hd_data',
      detailEndpoint: QUERY_KEYS.HOP_DONG
    }
  },
  {
    field: 'ma_dtt',
    headerName: 'Đợt thanh toán',
    width: 150,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<DotThanhToan>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.DOT_THANH_TOAN}/`}
        searchColumns={dotThanhToanSearchColumns}
        columnDisplay='ma_dtt'
        dialogTitle='Danh mục đợt thanh toán'
        value={params.row.ma_dtt_data?.ma_dtt || ''}
        onRowSelection={(row: BanHangRecord) => onCellValueChange(params.row.uuid, 'ma_dtt_data', row)}
        error={cellError?.(params.row.uuid, 'ma_dtt')}
        errorDisplay='tooltip'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_dtt',
      resultField: 'ma_dtt_data',
      detailEndpoint: QUERY_KEYS.DOT_THANH_TOAN
    }
  },
  {
    field: 'ma_ku',
    headerName: 'Khế ước',
    width: 150,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<KheUoc>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.KHE_UOC}/`}
        searchColumns={kheUocSearchColumns}
        columnDisplay='ma_ku'
        dialogTitle='Danh mục khế ước'
        value={params.row.ma_ku_data?.ma_ku || ''}
        onRowSelection={(row: BanHangRecord) => onCellValueChange(params.row.uuid, 'ma_ku_data', row)}
        error={cellError?.(params.row.uuid, 'ma_ku')}
        errorDisplay='tooltip'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_ku',
      resultField: 'ma_ku_data',
      detailEndpoint: QUERY_KEYS.KHE_UOC
    }
  },
  {
    field: 'ma_phi',
    headerName: 'Phí',
    width: 150,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<Phi>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.PHI}/`}
        searchColumns={phiSearchColumns}
        columnDisplay='ma_phi'
        dialogTitle='Danh mục phí'
        value={params.row.ma_phi_data?.ma_phi || ''}
        onRowSelection={(row: BanHangRecord) => onCellValueChange(params.row.uuid, 'ma_phi_data', row)}
        error={cellError?.(params.row.uuid, 'ma_phi')}
        errorDisplay='tooltip'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_phi',
      resultField: 'ma_phi_data',
      detailEndpoint: QUERY_KEYS.PHI
    }
  },
  {
    field: 'ma_sp',
    headerName: 'Sản phẩm',
    width: 150,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<VatTu>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.VAT_TU}/`}
        searchColumns={vatTuSearchColumns}
        columnDisplay='ma_vt'
        dialogTitle='Danh mục sản phẩm'
        value={params.row.ma_sp_data?.ma_vt || ''}
        onRowSelection={(row: BanHangRecord) => onCellValueChange(params.row.uuid, 'ma_sp_data', row)}
        error={cellError?.(params.row.uuid, 'ma_sp')}
        errorDisplay='tooltip'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_sp',
      resultField: 'ma_sp_data',
      detailEndpoint: QUERY_KEYS.VAT_TU
    }
  },
  {
    field: 'ma_lsx',
    headerName: 'Lệnh sản xuất',
    width: 150,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<BanHangRecord>
        type='text'
        searchEndpoint={'/'}
        searchColumns={lenhSanXuatSearchColumns}
        columnDisplay='ma_lsx'
        dialogTitle='Danh mục lệnh sản xuất'
        value={params.row.ma_lsx_data?.ma_lsx || ''}
        onRowSelection={(row: BanHangRecord) => onCellValueChange(params.row.uuid, 'ma_lsx_data', row)}
        error={cellError?.(params.row.uuid, 'ma_lsx')}
        errorDisplay='tooltip'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_lsx',
      resultField: 'ma_lsx_data',
      detailEndpoint: QUERY_KEYS.LENH_SAN_XUAT
    }
  },
  {
    field: 'ma_cp0',
    headerName: 'C/p không h/lệ',
    width: 150,
    renderCell: (params: DataTableRenderCellParams) => (
      <SearchField<BanHangRecord>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.CHI_PHI_KHONG_HOP_LE}/`}
        searchColumns={chiPhiKhongHopLeSearchColumns}
        columnDisplay='ma_cpkhl'
        dialogTitle='Danh mục chi phí không hợp lệ'
        value={params.row.ma_cp0_data?.ma_cp0 || ''}
        onRowSelection={(row: BanHangRecord) => onCellValueChange(params.row.uuid, 'ma_cp0_data', row)}
        error={cellError?.(params.row.uuid, 'ma_cp0')}
        errorDisplay='tooltip'
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_cp0',
      resultField: 'ma_cp0_data',
      detailEndpoint: QUERY_KEYS.CHI_PHI_KHONG_HOP_LE
    }
  },
  {
    field: 'sl_hd',
    headerName: 'Sl hoá đơn',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => <CellField name='sl_hd' type='text' value={params.row.sl_hd} />,
    nonWritable: true
  },
  {
    field: 'sl_px',
    headerName: 'Sl sản xuất',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField name='so_ct_dh' type='text' value={params.row.so_ct_dh} />
    ),
    nonWritable: true
  },
  {
    field: 'so_ct_hd',
    headerName: 'Số hợp đồng',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField name='line_dh' type='text' value={params.row.line_dh} />
    ),
    nonWritable: true
  },
  {
    field: 'line_hd',
    headerName: 'Dòng HĐ',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField name='line_hd' type='text' value={params.row.line_hd} />
    ),
    nonWritable: true
  },
  {
    field: 'so_ct_bg',
    headerName: 'Số báo giá',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField name='so_ct_hd' type='text' value={params.row.so_ct_hd} />
    ),
    nonWritable: true
  },
  {
    field: 'line_bg',
    headerName: 'Dòng BG',
    width: 120,
    renderCell: (params: DataTableRenderCellParams) => (
      <CellField name='line_hd' type='text' value={params.row.line_hd} />
    ),
    nonWritable: true
  }
];
